/**
 * dumping ground for stuff I cannot or have not otherwise catagorized and aggrigator of cross-cutting logic
 */

import _ = require("underscore");
import $ = require("jquery");
import Portal = require("portal-gis");
import deepExtend = require("./utils/deep-extend");

import Mediator = require("./mediator");
import BaseCommand = require("./commands/base-command");
import Extensions = require("./data/extensions");

import resx = require("./i18n/common");
import globals = require("../globals");
import { Dictionary } from "underscore";

const extensions = new Extensions();
const ticksPerDay = (new Date(2000, 1, 2)).valueOf() - (new Date(2000, 1, 1)).valueOf();

function isTrue(value: string|boolean) {
    return ("true" === value);
}

class Utils {

    // config
    options: Rhythm.GIS.Dictionary<string>;

    log(...args: any[]) {
        ("1" === globals.debug) && console.log.call(console, args);
    }

    /**
    replace all instances of a substring within a string
    @param find the substring to find
    @param replace the replacement value
    @param str the text to search
    */
    replaceAll(find: string, replace: string, str: string) {
        return str.replace(new RegExp(find, 'g'), replace);
    }

    /**
    jQuery workaround to find elements by class when the name contains a special character (a period or /)
    @param className the class name (can contain . and /)
    @param context the top-level dom element to search
    @returns the jQuery collection of matching nodes
    @see {@link https://learn.jquery.com/using-jquery-core/faq/how-do-i-select-an-element-by-an-id-that-has-characters-used-in-css-notation/}
    */
    byClass(className: string, context?: JQuery) {
        // jquery selector workaround, foo.bar -> .foo\.bar, foo/bar -> foo\/bar
        className = this.replaceAll("\\.", "\\.", className);
        className = "." + this.replaceAll("/", "\\/", className);
        return $(className, context);
    }

    /**
    Load a module or the default value
    @param fault the default value
    @param mid either the mid or the object to resolve
    @cb callback to execute once module has been resolved
    */
    withModule<T>(fault: T, mid: string | T, cb: (mod: T) => void) {
        this.log("loading module: ", mid);

        if (!mid) {
            cb(fault);
            return;
        }

        if (typeof mid !== "string") {
            cb(mid);
        } else {
            requirejs([mid],
                mod => {
                    cb(mod);
                },
                err => {
                    console.warn("invalid mid: ", mid);
                    cb(fault);
                });
        }
    }

    withCss(mid: string, cb?: () => void) {
        this.log("loading css: ", mid);
        let err = {};
        this.withModule(err, `xstyle/css!${requirejs.toUrl(mid)}`, args => {
            if (args === err) {
                this.log("css not found: ", mid);
            }
            cb && cb();
        });
    }

    withTemplate(mid: string, cb: (template) => void) {
        this.log("loading template: ", mid);
        this.withModule(null, mid, cb);
    }

    /**
     Converts an array to a map
    @param values array of id+value elements
    @returns hash map of [id]=value
     */
    asDictionary(values: Array<{ id?: string; value?: string; }>) {
        let result = <Rhythm.GIS.Dictionary<string>>{};
        for (let i = 0; i < values.length; i++) {
            let key = values[i];
            if (typeof key.id === "string") {
                let value = key.value;
                switch (typeof value) {
                    case "string":
                        // HACK03: intrim 'solution', not sure how to preserve nested data
                        if (0 === value.indexOf("{") && value.length === value.lastIndexOf("}")) {
                            value = JSON.parse(value);
                        }
                        result[key.id] = value;
                        break;

                    case "undefined":
                        result[key.id] = null; // ensures it will not inherit container options
                        break;

                    case "object":
                    default:
                        result[key.id] = value;
                        break;
                }
            } else {
                // perhaps there is no id?
            }
        }
        return result;
    }

    /** inverse of asDictionary */
    asOptions(dictionary: Rhythm.GIS.Dictionary<string>) {
        var result: Array<{ id?: string; value?: string; }> = [];
        _.keys(dictionary).forEach(key => {
            let value = dictionary[key];
            switch (typeof value) {
                case "string":
                    break;
                case "object":
                    // HACK03: how to preserve nested values?
                    value = value === null ? null : JSON.stringify(value);
                    break;
                case "undefined":
                    value = null;
                    break;
                default:
                    value = value.toString();
                    break;
            }

            if (value !== null) {
                result.push({
                    id: key,
                    value: value
                });
            }

        });

        return result;
    }

    setOption<T>(values: Array<{ id?: string; value?: T; }>, dictionary: Rhythm.GIS.Dictionary<T>) {
        for (var key in dictionary) {
            if (dictionary.hasOwnProperty(key)) {
                var value = dictionary[key];
                var options = values.filter(option => option.id === key);
                if (!options.length) {
                    values.push({ id: key, value: value });
                } else {
                    options.forEach(option => option.value = value);
                }
            }
        }
        return values;
    }

    ifOption<T>(options: Rhythm.GIS.Dictionary<T>, key: string, cb: (value: T, key: string, options: any) => void, elseCb?: (key: string, options: any) => void): T {
        if ("undefined" === typeof options) return null;

        let value = options[key];
        if ("undefined" === typeof value) {
            elseCb && elseCb(key, options);
            return null;
        }

        cb(value, key, options);
        return value;
    }

    caseOption<T>(options: Rhythm.GIS.Dictionary<T>, key: string, cb: Rhythm.GIS.Dictionary<(value: T, key: string, options: any) => void>): T {
        if ("undefined" === typeof options) return null;
        if ("undefined" === typeof options[key]) {
            this.log(`option undefined: ${key}`);
            return null;
        }

        let c = cb[<any>options[key]];
        if ("undefined" === typeof c) {
            this.log(`callback undefined: ${options[key]}`);
            return null;
        }
        c(options[key], key, options);
    }

    // command
    loadModules<T extends {
        id?: string,
        name?: string;
        mid?: string;
        disabled?: boolean
    }>(modules: T[], pathMapper?: (mod: T) => string) {
        let d = $.Deferred<{
            factory: Rhythm.GIS.Dictionary<any>;
            modules: T[];
        }>();

        // TODO: move this into configuration initialization
        modules.forEach(mod => {
            mod.id = mod.id || mod.name;
            mod.name = mod.name || mod.id;
            if (!mod.mid && pathMapper) {
                mod.mid = pathMapper(mod);
            }
            mod.mid = mod.mid || mod.id;
        });

        modules = modules.filter(c => !!c.mid && !c.disabled);
        let mids = _.uniq(modules.map(c => c.mid));

        requirejs(mids, (...args) => {
            let factory = <any>{};
            mids.forEach((m, i) => factory[m] = args[i]);
            d.resolve({
                factory: factory,
                modules: modules
            });
        });

        return d;
    }

    /**
     loads command modules; if no mid is specified defaults to app/commands/${id}
     */
    loadCommandModules(modules: Portal.CommandLeaf[]) {
        return this.loadModules(modules, mod => `app/commands/${mod.id}`);
    }

    loadControllerModules(modules: Portal.ControlLeaf[]) {
        return this.loadModules(modules, mod => `app/controls/${mod.id}`);
    }

    loadEventModules(modules: Portal.EventsLeaf[]) {
        return this.loadModules(modules, mod => `app/commands/${mod.id}`);
    }


    /**
    @param command executes the command describe by this configuration
    */
    execute(command: Portal.CommandLeaf, mediator: Mediator, args?: any) {
        this.log("loading command: ", command.mid);

        let inArgs = args && args.in;
        while (inArgs) {
            if (!!inArgs.command && inArgs.command === command) {
                console.error("command cycle detected, execution denied: ", command.id);
                return;
            }
            inArgs = inArgs.args && inArgs.args.in;
        }

        requirejs([command.mid], (Command) => {
            this.log(`executing command: ${command.id} from ${command.mid}`);
            if (Command.execute) {
                Command.execute(command, mediator, args);
            } else {
                let c = new Command(command, mediator, args);
                if (isTrue(c.options["disabled"])) return;
                if (isTrue(c.options["trace"])) console.log(command.id, c);
                c.execute();
            }
        });
    }

    // object
    data(item, path: string) {
        return path.split(".").reduce((obj, i) => obj && obj[i], item);
    }

    // array
    /**
    * Returns first element that meet the condition specified in a callback function.
    * @param test A function that accepts up to three arguments. The filter method calls the callbackfn function one time for each element in the array.
    */
    first<T>(arr: Array<T>, test: (value: T, index: number, array: T[]) => boolean): T {
        let result: T;
        arr.some((value, index, array) => {
            if (!!test(value, index, array)) {
                result = value;
                return true;
            }
        });
        return result;
    }

    distinct<T>(arr: Array<T>) {
        let result = <Array<T>>[];
        arr.forEach(v => {
            if (0 > result.indexOf(v)) result.push(v);
        });
        return result;
    }


    // string
    startsWith(value: string, pattern: string) {
        var index = value.indexOf(pattern);
        return (0 === index);
    }

    leftOf(value: string, pattern: string) {
        var index = value.indexOf(pattern);
        if (0 > index) return value;
        if (0 === index) return "";
        return value.substring(0, index);
    }

    rightOf(value: string, pattern: string) {
        var index = value.indexOf(pattern);
        if (0 > index) return value;
        return value.substring(index + 1);
    }

    // date

    addDay(date: Date, days: number = 1) {
        return new Date(date.valueOf() + days * ticksPerDay);
    }


    defaults<T, U>(o: T, clone: U) {
        return deepExtend(o, clone);
    }

    deep<T>(o: T) {
        return <T>JSON.parse(JSON.stringify(o));
    }

    /**
    Forces the existence of an extension container for an object
    @param o the object of interest
    @param [ext] sets these value on the extension object
    */
    extend<T, U>(o: T, ext?: U) {
        return extensions.extend(o, ext);
    }

    /**
    TODO: remove this and use "bind" where it is referenced (preferably mediator.bind)
    Get the extension lookup identifier for this object
    @param o the object of interest
    @param [force=true] if true, forces the identifier to exist
    */
    getExtensionKey(o: any, force = false) {
        return extensions.getExtensionKey(o, force);
    }

    // async
    when<T>(subordinates: Array<T | JQueryDeferred<T>>) {
        var d = $.Deferred<Array<T>>();
        let r = ($.when.apply($, subordinates));
        r.done((...resolved: T[]) => {
            d.resolve(resolved);
        });
        r.fail(err => {
            console.error("subordinate failure:", err);
            d.reject(err);
        });
        return d;
    }

    on(view, event, cb) {
        view.on(event, cb);
        return {
            off: () => view.off(event, cb)
        };
    }

    /**
     * callback if the value exists otherwise wait until it does exist
     */
    once<T>(model: Backbone.Model, field: string, cb: (v: T) => void) {
        var v = model.get(field);
        if ("undefined" !== typeof v) {
            cb(v);
            return;
        }

        model.once("change:" + field, (model, value) => {
            cb(value);
        });

    }

    load(items: Array<{ mid: string }>, mediator) {

        var mids = items.filter(c => !!c.mid).map(c => c.mid);
        var factory = {};
        var d = $.Deferred();

        requirejs(mids, (...args) => {
            mids.forEach((m, i) => factory[m] = args[i]);
            var objects = items.map(c => new (factory[c.mid])(c, mediator));
            d.resolveWith(this, objects);
        });

        return d;
    }

    translate(value: string, locale = globals.locale) {
        return this.template(value, resx(locale));
    }

    template(value: string, data: Dictionary<string> = resx(globals.locale), depth = 2) {
        // if not an interpolating template assume simple value
        if (-1 === value.indexOf("<%=")) return value;
        // execute the template
        try {
            value = _.template(value)(data);
        } catch (ex) {
            console.warn(`consider using <%- instead of <%= for ${value}`);
            return value;
        }
        // if calling depth exceeded return value otherwise test for nested templates
        return (0 >= depth) ? value : this.template(value, data, depth - 1);
    }

    split(value: string, separator = ",") {
        return value.split(separator).map(v => v.trim());
    }
}

let utils = new Utils();

utils.options = {};

export = utils;
